/**
 * The route planner's floor (integration plan slice 4): what the fog of war
 * shows, never the whole state. A tile is floor for the planner only if
 * game-core calls it walkable and the view has explored it (`view.explored()`);
 * a unit is on the planner's grid only where the view draws it (the
 * renderer's rule: a party unit always, any other standing unit on an
 * explored tile). So a route never plans through unexplored corridor and never
 * bends around a unit the player cannot see. Every step is still game-core's:
 * the first step of `walkTable` over this view. A target holding a unit the
 * player sees is walked up to, not into (`walkIn`: the walk stops one step
 * short, so no move is refused).
 */
import { type Dir, type Event, type GridView, type Pos, type RunState, gridView, stepIn, walkIn, walkTable } from '@ab/game-core';

/** Game-core's grid of `state`, cut to what the player sees: explored walkable tiles and the units drawn on screen. */
export const seenGrid = (state: RunState, explored: readonly boolean[]): GridView => {
  const g = gridView(state);
  const seen = (p: Pos): boolean => p.x >= 0 && p.y >= 0 && p.x < g.width && p.y < g.height && explored[p.y * g.width + p.x] === true;
  const party = new Set(state.units.filter((u) => u.side === 'party').map((u) => u.id));
  return {
    ...g,
    isFloor: (p) => seen(p) && g.isFloor(p),
    unitAt: (p) => {
      const id = g.unitAt(p);
      return id !== null && (party.has(id) || seen(p)) ? id : null;
    },
  };
};

/**
 * The next step of a route from `from` to `target` over `seenGrid`, or null
 * when there is none: no walk through explored tiles, already there, or
 * beside a unit standing on the target.
 */
export const routeStep = (state: RunState, explored: readonly boolean[], from: Pos, target: Pos): Dir | null => {
  const g = seenGrid(state, explored);
  const table = walkTable(g, from, null);
  const walk = g.unitAt(target) === null ? stepIn(table, target) : walkIn(table, target);
  return walk?.first ?? null;
};

/** The events a route walks on through; any other stops it (integration plan slice 4). */
const routeEvents: ReadonlySet<Event['type']> = new Set(['moved', 'turned', 'round_ended']);

/**
 * Whether a route walks on through an event: a move, a turn, the end of a round, and natural regeneration (ADR 0032,
 * Q39: game-core's `healed` with cause `regen`, and the `passive_triggered` of a passive that scaled that heal, such
 * as Flower Bloom below half HP, which carries the same `cause`); any other event, a potion's or a skill's heal
 * included, stops it.
 */
export const routeGoesOn = (e: Event): boolean =>
  routeEvents.has(e.type) || ((e.type === 'healed' || e.type === 'passive_triggered') && e.cause === 'regen');
